import { useState } from 'react';
import { toast } from 'sonner';
import { Cake, Download, KeyRound, Lock, LockOpen, Nfc, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PhoneNumberInput } from '@/components/ui/PhoneNumberInput';
import { Textarea } from '@/components/ui/Textarea';
import { Label } from '@/components/ui/Label';
import { Switch } from '@/components/ui/Switch';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { AchievementGrid, LevelBadge, StatsStrip } from '@/components/work/WorkAchievements';
import { AvatarPreview } from '@/components/digitalOffice/AvatarPreview';
import { describeWorkError } from '@/features/workMode/api';
import { useUnlockCredentials, useWorkProfileStats } from '@/features/workMode/hooks';
import type { WorkCredentialStatus } from '@/features/workMode/types';
import type { WorkPerson } from '@/features/workMode/useLookups';
import {
  formatCurrency,
  formatDate,
  formatDateOnly,
  inputDollarsToCents,
  todayIsoDate,
} from '@/lib/formatters';
import type { EmployeeDraft } from '@/features/hr/hooks';
import {
  useAddTimelineEvent,
  useCreateGoal,
  useDeleteEmployeeDocument,
  useDeleteGoal,
  useDeleteTimelineEvent,
  useEmployeeCompensation,
  useEmployeeDocuments,
  useEmployeeGoals,
  useEmployeeTimeline,
  useRecordCompensation,
  useUpdateGoalStatus,
  useUploadEmployeeDocument,
  openEmployeeDocument,
} from '@/features/hr/hooks';
import {
  DOCUMENT_CATEGORIES,
  EMPLOYMENT_STATUSES,
  EMPLOYMENT_TYPES,
  GOAL_STATUSES,
  PAY_REASONS,
  SHIRT_SIZES,
  TIMELINE_KINDS,
  getTimezones,
  labelOf,
} from '@/features/hr/model';
import type {
  EmployeeDocumentCategory,
  EmployeeEmploymentStatus,
  EmployeeEmploymentType,
  EmployeeGoalStatus,
  EmployeePayReason,
  EmployeePayType,
  EmployeeTimelineKind,
} from '@/types/database';
import { Field } from './Field';

const NONE = 'none';

function patch<K extends keyof EmployeeDraft>(
  draft: EmployeeDraft,
  onChange: (next: EmployeeDraft) => void,
  key: K,
  value: EmployeeDraft[K],
) {
  onChange({ ...draft, [key]: value });
}

export function ProfileFields({
  draft,
  onChange,
}: {
  draft: EmployeeDraft;
  onChange: (next: EmployeeDraft) => void;
}) {
  const set = <K extends keyof EmployeeDraft>(key: K, value: EmployeeDraft[K]) =>
    patch(draft, onChange, key, value);
  const zones = getTimezones();
  const zoneOptions =
    draft.timezone && !zones.includes(draft.timezone) ? [draft.timezone, ...zones] : zones;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>What we call them</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Preferred name"
            htmlFor="preferred_name"
          >
            <Input
              id="preferred_name"
              value={draft.preferred_name}
              onChange={(e) => set('preferred_name', e.target.value)}
              placeholder="Alex"
            />
          </Field>
          <Field label="Atlas name" htmlFor="full_name">
            <Input
              id="full_name"
              value={draft.full_name}
              onChange={(e) => set('full_name', e.target.value)}
            />
          </Field>
          <Field label="Pronouns" htmlFor="pronouns">
            <Input
              id="pronouns"
              value={draft.pronouns}
              onChange={(e) => set('pronouns', e.target.value)}
              placeholder="she/her"
            />
          </Field>
          <Field
            label="Birthday"
            htmlFor="date_of_birth"
          >
            <Input
              id="date_of_birth"
              type="date"
              value={draft.date_of_birth}
              onChange={(e) => set('date_of_birth', e.target.value)}
            />
          </Field>
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2 sm:col-span-2">
            <div>
              <p className="text-sm font-medium text-foreground">Celebrate their birthday</p>
            </div>
            <Switch
              checked={draft.celebrate_birthday}
              onCheckedChange={(v) => set('celebrate_birthday', v)}
              aria-label="Celebrate their birthday"
            />
          </div>
          <Field label="Phone" htmlFor="phone">
            <PhoneNumberInput id="phone" value={draft.phone} onChange={(v) => set('phone', v)} />
          </Field>
          <Field label="Personal email" htmlFor="personal_email">
            <Input
              id="personal_email"
              type="email"
              value={draft.personal_email}
              onChange={(e) => set('personal_email', e.target.value)}
            />
          </Field>
          <Field label="Time zone" htmlFor="timezone">
            <select
              id="timezone"
              value={draft.timezone}
              onChange={(e) => set('timezone', e.target.value)}
              className="flex h-10 w-full rounded-lg border border-input bg-card px-3 py-2 text-sm text-foreground"
            >
              <option value="">—</option>
              {zoneOptions.map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </select>
          </Field>
          <div className="sm:col-span-2">
            <Field
              label="About them"
              htmlFor="bio"
            >
              <Textarea
                id="bio"
                rows={3}
                value={draft.bio}
                onChange={(e) => set('bio', e.target.value)}
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Home</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Field label="Address" htmlFor="address_line1">
              <Input
                id="address_line1"
                value={draft.address_line1}
                onChange={(e) => set('address_line1', e.target.value)}
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Address line 2" htmlFor="address_line2">
              <Input
                id="address_line2"
                value={draft.address_line2}
                onChange={(e) => set('address_line2', e.target.value)}
              />
            </Field>
          </div>
          <Field label="City" htmlFor="city">
            <Input id="city" value={draft.city} onChange={(e) => set('city', e.target.value)} />
          </Field>
          <Field label="State" htmlFor="state">
            <Input id="state" value={draft.state} onChange={(e) => set('state', e.target.value)} />
          </Field>
          <Field label="Postal code" htmlFor="postal_code">
            <Input
              id="postal_code"
              value={draft.postal_code}
              onChange={(e) => set('postal_code', e.target.value)}
            />
          </Field>
          <Field label="Country" htmlFor="country">
            <Input
              id="country"
              value={draft.country}
              onChange={(e) => set('country', e.target.value)}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>If something happens</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <Field label="Emergency contact" htmlFor="emergency_contact_name">
            <Input
              id="emergency_contact_name"
              value={draft.emergency_contact_name}
              onChange={(e) => set('emergency_contact_name', e.target.value)}
            />
          </Field>
          <Field label="Relationship" htmlFor="emergency_contact_relationship">
            <Input
              id="emergency_contact_relationship"
              value={draft.emergency_contact_relationship}
              onChange={(e) => set('emergency_contact_relationship', e.target.value)}
            />
          </Field>
          <Field label="Phone" htmlFor="emergency_contact_phone">
            <PhoneNumberInput
              id="emergency_contact_phone"
              value={draft.emergency_contact_phone}
              onChange={(v) => set('emergency_contact_phone', v)}
            />
          </Field>
          <Field label="Second contact" htmlFor="secondary_emergency_name">
            <Input
              id="secondary_emergency_name"
              value={draft.secondary_emergency_name}
              onChange={(e) => set('secondary_emergency_name', e.target.value)}
            />
          </Field>
          <Field label="Relationship" htmlFor="secondary_emergency_relationship">
            <Input
              id="secondary_emergency_relationship"
              value={draft.secondary_emergency_relationship}
              onChange={(e) => set('secondary_emergency_relationship', e.target.value)}
            />
          </Field>
          <Field label="Phone" htmlFor="secondary_emergency_phone">
            <PhoneNumberInput
              id="secondary_emergency_phone"
              value={draft.secondary_emergency_phone}
              onChange={(v) => set('secondary_emergency_phone', v)}
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cake className="h-4 w-4 text-muted-foreground" />
            How to take care of them
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Shirt size" htmlFor="shirt_size">
            <Select
              value={draft.shirt_size || NONE}
              onValueChange={(v) => set('shirt_size', v === NONE ? '' : v)}
            >
              <SelectTrigger id="shirt_size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>—</SelectItem>
                {SHIRT_SIZES.map((size) => (
                  <SelectItem key={size} value={size}>
                    {size}
                  </SelectItem>
                ))}
                {draft.shirt_size &&
                  !SHIRT_SIZES.includes(draft.shirt_size as (typeof SHIRT_SIZES)[number]) && (
                    <SelectItem value={draft.shirt_size}>{draft.shirt_size}</SelectItem>
                  )}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Languages" htmlFor="languages">
            <Input
              id="languages"
              value={draft.languages}
              onChange={(e) => set('languages', e.target.value)}
              placeholder="English, Spanish"
            />
          </Field>
          <div className="sm:col-span-2">
            <Field
              label="Allergies"
              htmlFor="allergies"
            >
              <Textarea
                id="allergies"
                rows={2}
                value={draft.allergies}
                onChange={(e) => set('allergies', e.target.value)}
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field
              label="Dietary notes"
              htmlFor="dietary_notes"
            >
              <Textarea
                id="dietary_notes"
                rows={2}
                value={draft.dietary_notes}
                onChange={(e) => set('dietary_notes', e.target.value)}
              />
            </Field>
          </div>
          <Field label="Coffee or tea" htmlFor="coffee_order">
            <Input
              id="coffee_order"
              value={draft.coffee_order}
              onChange={(e) => set('coffee_order', e.target.value)}
              placeholder="Oat latte, one sugar"
            />
          </Field>
          <Field label="Favorite snack" htmlFor="favorite_snack">
            <Input
              id="favorite_snack"
              value={draft.favorite_snack}
              onChange={(e) => set('favorite_snack', e.target.value)}
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="What they light up about" htmlFor="interests">
              <Textarea
                id="interests"
                rows={2}
                value={draft.interests}
                onChange={(e) => set('interests', e.target.value)}
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field
              label="How they like to be celebrated"
              htmlFor="celebration_notes"
            >
              <Textarea
                id="celebration_notes"
                rows={2}
                value={draft.celebration_notes}
                onChange={(e) => set('celebration_notes', e.target.value)}
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notes only HR sees</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            id="hr_notes"
            rows={4}
            value={draft.hr_notes}
            onChange={(e) => set('hr_notes', e.target.value)}
            placeholder="Context a manager should remember, not a substitute for a conversation."
          />
        </CardContent>
      </Card>
    </div>
  );
}

export function RoleFields({
  draft,
  onChange,
  managers,
}: {
  draft: EmployeeDraft;
  onChange: (next: EmployeeDraft) => void;
  managers: { id: string; name: string }[];
}) {
  const set = <K extends keyof EmployeeDraft>(key: K, value: EmployeeDraft[K]) =>
    patch(draft, onChange, key, value);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Role</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <Field label="Title" htmlFor="job_title">
          <Input
            id="job_title"
            value={draft.job_title}
            onChange={(e) => set('job_title', e.target.value)}
            placeholder="Assembler"
          />
        </Field>
        <Field label="Position" htmlFor="position">
          <Input
            id="position"
            value={draft.position}
            onChange={(e) => set('position', e.target.value)}
            placeholder="Day shift, final assembly"
          />
        </Field>
        <Field label="Department" htmlFor="department">
          <Input
            id="department"
            value={draft.department}
            onChange={(e) => set('department', e.target.value)}
            placeholder="Operations"
          />
        </Field>
        <Field label="Manager" htmlFor="manager_id">
          <Select
            value={draft.manager_id || NONE}
            onValueChange={(v) => set('manager_id', v === NONE ? '' : v)}
          >
            <SelectTrigger id="manager_id">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>No manager</SelectItem>
              {managers.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Employment type" htmlFor="employment_type">
          <Select
            value={draft.employment_type || NONE}
            onValueChange={(v) =>
              set('employment_type', v === NONE ? '' : (v as EmployeeEmploymentType))
            }
          >
            <SelectTrigger id="employment_type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>—</SelectItem>
              {EMPLOYMENT_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Status" htmlFor="employment_status">
          <Select
            value={draft.employment_status}
            onValueChange={(v) => set('employment_status', v as EmployeeEmploymentStatus)}
          >
            <SelectTrigger id="employment_status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {EMPLOYMENT_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Work location" htmlFor="work_location">
          <Input
            id="work_location"
            value={draft.work_location}
            onChange={(e) => set('work_location', e.target.value)}
            placeholder="230 Stanford Parkway"
          />
        </Field>
        <Field
          label="Start date"
          htmlFor="start_date"
        >
          <Input
            id="start_date"
            type="date"
            value={draft.start_date}
            onChange={(e) => set('start_date', e.target.value)}
          />
        </Field>
      </CardContent>
    </Card>
  );
}

export function WorkModeCard({
  person,
  credential,
  onSetPin,
  onSetBadge,
}: {
  person: WorkPerson;
  credential: WorkCredentialStatus | undefined;
  onSetPin: () => void;
  onSetBadge: () => void;
}) {
  const { data: stats, isLoading } = useWorkProfileStats(person.id);
  const unlock = useUnlockCredentials();
  const locked = !!credential?.locked_until && Date.parse(credential.locked_until) > Date.now();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Work Mode</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex h-[72px] w-[72px] items-center justify-center overflow-hidden rounded-2xl bg-secondary">
            <AvatarPreview config={person.config} size={72} />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              {locked ? (
                <Badge variant="warning-soft">
                  <Lock className="mr-1 h-3 w-3" />
                  PIN locked
                </Badge>
              ) : credential?.has_pin ? (
                <Badge variant="success-soft">
                  <KeyRound className="mr-1 h-3 w-3" />
                  PIN set
                </Badge>
              ) : (
                <Badge variant="muted">No PIN yet</Badge>
              )}
              {credential?.has_badge ? (
                <Badge variant="info-soft">
                  <Nfc className="mr-1 h-3 w-3" />
                  Badge …{credential.badge_last4}
                </Badge>
              ) : (
                <Badge variant="muted">No badge</Badge>
              )}
            </div>
            {credential?.pin_set_at ? (
              <p className="text-xs text-muted-foreground">
                Last set {formatDate(credential.pin_set_at)}.
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button type="button" size="sm" onClick={onSetPin}>
                <KeyRound className="h-3.5 w-3.5" />
                {credential?.has_pin ? 'Reset PIN' : 'Set PIN'}
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={onSetBadge}>
                <Nfc className="h-3.5 w-3.5" />
                Badge
              </Button>
              {locked && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={unlock.isPending}
                  onClick={() =>
                    unlock.mutate(
                      { userId: person.id },
                      {
                        onSuccess: () => toast.success('PIN unlocked.'),
                        onError: (e) => toast.error(describeWorkError(e)),
                      },
                    )
                  }
                >
                  <LockOpen className="h-3.5 w-3.5" />
                  Unlock
                </Button>
              )}
            </div>
          </div>
        </div>

        {isLoading ? (
          <Skeleton className="h-24" />
        ) : stats ? (
          <div className="space-y-4">
            <LevelBadge stats={stats} />
            <StatsStrip stats={stats} />
            <AchievementGrid stats={stats} compact />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No floor stats yet.</p>
        )}
      </CardContent>
    </Card>
  );
}

export function PayPanel({
  profileId,
  payRateCents,
  payType,
}: {
  profileId: string;
  payRateCents: number | null;
  payType: EmployeePayType;
}) {
  const { data: history = [], isLoading } = useEmployeeCompensation(profileId);
  const record = useRecordCompensation();
  const [effectiveDate, setEffectiveDate] = useState(todayIsoDate());
  const [type, setType] = useState<EmployeePayType>(payType);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState<EmployeePayReason>(payRateCents == null ? 'hire' : 'raise');
  const [note, setNote] = useState('');

  const save = async () => {
    let cents: number | null;
    try {
      cents = inputDollarsToCents(amount);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Invalid amount.');
      return;
    }
    if (cents == null) {
      toast.error('Enter the new rate.');
      return;
    }
    if (!effectiveDate) {
      toast.error('Pick the date this rate starts.');
      return;
    }
    try {
      await record.mutateAsync({
        profileId,
        effectiveDate,
        payType: type,
        payRateCents: cents,
        reason,
        note: note.trim() || null,
      });
      toast.success('Pay change recorded.');
      setAmount('');
      setNote('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not record pay.');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Current rate</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-medium tabular-nums text-foreground">
            {payRateCents != null
              ? `${formatCurrency(payRateCents)}${payType === 'hourly' ? '/hr' : '/yr'}`
              : 'No rate yet'}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Record a change</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Effective date" htmlFor="pay-date">
            <Input
              id="pay-date"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
          </Field>
          <Field label="Reason" htmlFor="pay-reason">
            <Select value={reason} onValueChange={(v) => setReason(v as EmployeePayReason)}>
              <SelectTrigger id="pay-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAY_REASONS.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Pay type" htmlFor="pay-type">
            <Select value={type} onValueChange={(v) => setType(v as EmployeePayType)}>
              <SelectTrigger id="pay-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hourly">Hourly</SelectItem>
                <SelectItem value="salary">Salary (annual)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field
            label={type === 'hourly' ? 'Hourly rate ($)' : 'Annual salary ($)'}
            htmlFor="pay-amount"
          >
            <Input
              id="pay-amount"
              inputMode="decimal"
              placeholder={type === 'hourly' ? '28.50' : '75000'}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Note" htmlFor="pay-note">
              <Input
                id="pay-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Annual review, new role, correction of a typo"
              />
            </Field>
          </div>
          <div>
            <Button type="button" onClick={save} disabled={record.isPending}>
              {record.isPending ? 'Saving…' : 'Record'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>History</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-24" />
          ) : history.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {payRateCents != null
                ? `Current rate is ${formatCurrency(payRateCents)} ${payType === 'hourly' ? 'an hour' : 'a year'}.`
                : 'No pay history yet.'}
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Effective</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Rate</TableHead>
                  <TableHead>Note</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap">
                      {formatDateOnly(row.effective_date)}
                    </TableCell>
                    <TableCell>{labelOf(PAY_REASONS, row.reason)}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatCurrency(row.pay_rate_cents)}
                      {row.pay_type === 'hourly' ? '/hr' : '/yr'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{row.note || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export function GoalsPanel({ profileId }: { profileId: string }) {
  const { data: goals = [], isLoading } = useEmployeeGoals(profileId);
  const create = useCreateGoal();
  const update = useUpdateGoalStatus();
  const remove = useDeleteGoal();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetDate, setTargetDate] = useState('');

  const add = async () => {
    if (!title.trim()) {
      toast.error('Give the goal a name.');
      return;
    }
    try {
      await create.mutateAsync({
        profileId,
        title: title.trim(),
        description: description.trim() || null,
        targetDate: targetDate || null,
      });
      setTitle('');
      setDescription('');
      setTargetDate('');
      toast.success('Goal added.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add the goal.');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>A goal with the company</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4">
          <Field label="Goal" htmlFor="goal-title">
            <Input
              id="goal-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Lead a station on their own"
            />
          </Field>
          <Field label="What done looks like" htmlFor="goal-description">
            <Textarea
              id="goal-description"
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Field>
          <Field label="Target date" htmlFor="goal-date">
            <Input
              id="goal-date"
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
            />
          </Field>
          <div>
            <Button type="button" onClick={add} disabled={create.isPending}>
              {create.isPending ? 'Adding…' : 'Add goal'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Skeleton className="h-24" />
      ) : goals.length === 0 ? (
        <p className="text-sm text-muted-foreground">No goals yet.</p>
      ) : (
        <ul className="space-y-3">
          {goals.map((goal) => (
            <li key={goal.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-foreground">{goal.title}</p>
                  {goal.description && (
                    <p className="mt-1 text-sm text-muted-foreground">{goal.description}</p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {goal.target_date
                      ? `Target ${formatDateOnly(goal.target_date)}`
                      : 'No target date'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select
                    value={goal.status}
                    onValueChange={(v) =>
                      update.mutate(
                        { id: goal.id, profileId, status: v as EmployeeGoalStatus },
                        {
                          onError: (e) =>
                            toast.error(e instanceof Error ? e.message : 'Could not update.'),
                        },
                      )
                    }
                  >
                    <SelectTrigger className="w-[140px]" aria-label="Goal status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {GOAL_STATUSES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label="Delete goal"
                    onClick={() => {
                      if (!window.confirm(`Delete “${goal.title}”?`)) return;
                      remove.mutate(
                        { id: goal.id, profileId },
                        {
                          onError: (e) =>
                            toast.error(e instanceof Error ? e.message : 'Could not delete.'),
                        },
                      );
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentsPanel({ profileId }: { profileId: string }) {
  const { data: docs = [], isLoading } = useEmployeeDocuments(profileId);
  const upload = useUploadEmployeeDocument();
  const remove = useDeleteEmployeeDocument();
  const [category, setCategory] = useState<EmployeeDocumentCategory>('offer_letter');
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileKey, setFileKey] = useState(0);

  const save = async () => {
    if (!file) {
      toast.error('Choose a file.');
      return;
    }
    try {
      await upload.mutateAsync({
        profileId,
        file,
        category,
        title: title.trim() || file.name,
        notes: notes.trim() || null,
      });
      setTitle('');
      setNotes('');
      setFile(null);
      setFileKey((key) => key + 1);
      toast.success('Document saved.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not upload.');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Employment documents</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Kind" htmlFor="doc-category">
            <Select
              value={category}
              onValueChange={(v) => setCategory(v as EmployeeDocumentCategory)}
            >
              <SelectTrigger id="doc-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DOCUMENT_CATEGORIES.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Title" htmlFor="doc-title">
            <Input
              id="doc-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="2026 offer letter"
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="File" htmlFor="doc-file">
              <Input
                id="doc-file"
                key={fileKey}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt,application/pdf,image/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Note" htmlFor="doc-notes">
              <Input id="doc-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </div>
          <div>
            <Button type="button" onClick={save} disabled={upload.isPending}>
              {upload.isPending ? 'Uploading…' : 'Upload'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Skeleton className="h-24" />
      ) : docs.length === 0 ? (
        <p className="text-sm text-muted-foreground">No documents yet.</p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border bg-card">
          {docs.map((doc) => (
            <li
              key={doc.id}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">{doc.title}</p>
                <p className="text-xs text-muted-foreground">
                  {labelOf(DOCUMENT_CATEGORIES, doc.category)} · {doc.file_name} ·{' '}
                  {formatBytes(doc.byte_size)} · {formatDateOnly(doc.created_at.slice(0, 10))}
                </p>
              </div>
              <div className="flex gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    openEmployeeDocument(doc.storage_path).catch((err) =>
                      toast.error(err instanceof Error ? err.message : 'Could not open the file.'),
                    )
                  }
                >
                  <Download className="h-3.5 w-3.5" />
                  Open
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!window.confirm(`Delete “${doc.title}”?`)) return;
                    remove.mutate(
                      { id: doc.id, profileId, storagePath: doc.storage_path },
                      {
                        onSuccess: () => toast.success('Document deleted.'),
                        onError: (e) =>
                          toast.error(e instanceof Error ? e.message : 'Could not delete.'),
                      },
                    );
                  }}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function TimelinePanel({ profileId }: { profileId: string }) {
  const { data: events = [], isLoading } = useEmployeeTimeline(profileId);
  const add = useAddTimelineEvent();
  const remove = useDeleteTimelineEvent();
  const [occurredOn, setOccurredOn] = useState(todayIsoDate());
  const [kind, setKind] = useState<EmployeeTimelineKind>('recognition');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  const save = async () => {
    if (!title.trim()) {
      toast.error('Give the moment a title.');
      return;
    }
    if (!occurredOn) {
      toast.error('Pick a date.');
      return;
    }
    try {
      await add.mutateAsync({
        profileId,
        occurredOn,
        kind,
        title: title.trim(),
        body: body.trim() || null,
      });
      setTitle('');
      setBody('');
      toast.success('Added to their timeline.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not add that.');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Add to the timeline</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Date" htmlFor="tl-date">
            <Input
              id="tl-date"
              type="date"
              value={occurredOn}
              onChange={(e) => setOccurredOn(e.target.value)}
            />
          </Field>
          <Field label="Kind" htmlFor="tl-kind">
            <Select value={kind} onValueChange={(v) => setKind(v as EmployeeTimelineKind)}>
              <SelectTrigger id="tl-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMELINE_KINDS.map((k) => (
                  <SelectItem key={k.value} value={k.value}>
                    {k.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Title" htmlFor="tl-title">
              <Input
                id="tl-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Caught a safety issue before it shipped"
              />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="tl-body" className="mb-1.5 block">
              Details
            </Label>
            <Textarea
              id="tl-body"
              rows={2}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
          </div>
          <div>
            <Button type="button" onClick={save} disabled={add.isPending}>
              {add.isPending ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {isLoading ? (
        <Skeleton className="h-24" />
      ) : events.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing on the timeline yet.</p>
      ) : (
        <ol className="space-y-3">
          {events.map((event) => (
            <li key={event.id} className="flex gap-3">
              <div className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-info" />
              <div className="min-w-0 flex-1 rounded-lg border border-border bg-card px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">
                      {formatDateOnly(event.occurred_on)} · {labelOf(TIMELINE_KINDS, event.kind)}
                    </p>
                    <p className="mt-0.5 font-medium text-foreground">{event.title}</p>
                    {event.body && (
                      <p className="mt-1 text-sm text-muted-foreground">{event.body}</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    aria-label="Delete timeline entry"
                    onClick={() => {
                      if (!window.confirm(`Remove “${event.title}” from the timeline?`)) return;
                      remove.mutate(
                        { id: event.id, profileId },
                        {
                          onError: (e) =>
                            toast.error(e instanceof Error ? e.message : 'Could not delete.'),
                        },
                      );
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
